import { useMemo } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Folder, File as FileIcon, Trash2, Users } from "lucide-react";

import { Spinner } from "@/components/ui/Spinner";

import { twoDriveApi } from "../api";
import { useSharedByMe } from "../hooks";
import { DRIVE_SORT_KEYS, shareSortRow, sortBy, useSortState } from "../sorting";
import { formatBytes, formatDateTime } from "../utils";
import { SortHeader } from "./SortHeader";

/** Jak w „Udostępnione mi", z odbiorcą zamiast udostępniającego i koszem
 * zamiast pobierania. Nagłówek i wiersze muszą dzielić tę klasę. */
const ROW_GRID =
  "grid items-center gap-3 grid-cols-[minmax(0,1fr)_4rem_1.75rem]" +
  " sm:grid-cols-[minmax(0,1fr)_5rem_8rem_4rem_1.75rem]" +
  " lg:grid-cols-[minmax(0,1fr)_9rem_11rem_5rem_8rem_4rem_1.75rem]" +
  " xl:grid-cols-[minmax(0,1fr)_9rem_9rem_11rem_5rem_8rem_4rem_1.75rem]";

const CELL_TEXT = "text-xs text-[var(--color-text-muted)]";
const HEAD_TEXT = "text-xs font-mono uppercase tracking-wider text-[var(--color-text-muted)]";

export function SharedByMeList() {
  const { data: shares = [], isLoading } = useSharedByMe();
  const [sort, toggleSort] = useSortState(DRIVE_SORT_KEYS);
  const rows = useMemo(() => sortBy(shares, shareSortRow, sort), [shares, sort]);
  const qc = useQueryClient();
  const revoke = useMutation({
    mutationFn: (shareId: string) => twoDriveApi.deleteShare(shareId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["two-drive", "shared-by-me"] }),
  });

  if (isLoading) {
    return (
      <div className="py-10 flex justify-center">
        <Spinner />
      </div>
    );
  }

  if (shares.length === 0) {
    return (
      <div className="py-10 text-center text-sm text-[var(--color-text-muted)]">
        Nie udostępniasz jeszcze żadnych plików.
      </div>
    );
  }

  return (
    <ul className="divide-y divide-[var(--color-border)] border border-[var(--color-border)]">
      <li className={`${ROW_GRID} group px-3 py-1.5 bg-[var(--color-bg-base)]`}>
        <SortHeader label="Nazwa" sortKey="name" sort={sort} onSort={toggleSort} />
        <SortHeader
          label="Dodał/a"
          sortKey="uploader"
          sort={sort}
          onSort={toggleSort}
          className="hidden lg:inline-flex"
        />
        <SortHeader
          label="Utworzono"
          sortKey="created"
          sort={sort}
          onSort={toggleSort}
          className="hidden xl:inline-flex"
        />
        <SortHeader
          label="Ostatnia modyfikacja"
          sortKey="modified"
          sort={sort}
          onSort={toggleSort}
          className="hidden lg:inline-flex"
        />
        <SortHeader
          label="Rozmiar"
          sortKey="size"
          sort={sort}
          onSort={toggleSort}
          className="hidden sm:inline-flex justify-end"
        />
        <span className={`hidden sm:block truncate ${HEAD_TEXT}`}>Odbiorca</span>
        <span className={`truncate ${HEAD_TEXT}`}>Dostęp</span>
      </li>

      {rows.map((s) => {
        const isFolder = s.node_type === "folder";
        const uploader = s.node_uploaded_by_name || "—";
        return (
          <li key={s.id} className={`${ROW_GRID} px-3 py-2 hover:bg-[var(--color-bg-base)]`}>
            <span className="flex items-center gap-2 min-w-0">
              {isFolder ? (
                <Folder size={16} className="text-blue-500 flex-shrink-0" />
              ) : (
                <FileIcon size={16} className="text-[var(--color-text-muted)] flex-shrink-0" />
              )}
              <span className="min-w-0">
                <span className="block truncate text-sm">{s.node_name}</span>
                {/* Wąski ekran: dane z ukrytych kolumn schodzą pod nazwę. */}
                <span className="block text-[11px] leading-tight text-[var(--color-text-muted)] lg:hidden">
                  {!isFolder && <span className="sm:hidden">{formatBytes(s.node_size_bytes)} · </span>}
                  {uploader} · zm. {formatDateTime(s.node_updated_at)}
                  <span className="sm:hidden"> · dla {s.target.name}</span>
                </span>
              </span>
            </span>

            <span className={`hidden lg:block truncate ${CELL_TEXT}`} title={uploader}>
              {uploader}
            </span>
            <span className={`hidden xl:block truncate font-mono ${CELL_TEXT}`}>
              {formatDateTime(s.node_created_at)}
            </span>
            <span className={`hidden lg:block truncate font-mono ${CELL_TEXT}`}>
              {formatDateTime(s.node_updated_at)}
            </span>
            <span className={`hidden sm:block text-right font-mono ${CELL_TEXT}`}>
              {isFolder ? "" : formatBytes(s.node_size_bytes)}
            </span>
            <span
              className={`hidden sm:inline-flex items-center gap-1 min-w-0 ${CELL_TEXT}`}
              title={s.target.name}
            >
              {s.target.type === "group" && <Users size={12} className="flex-shrink-0" />}
              <span className="truncate">{s.target.name}</span>
            </span>
            <span className="text-[10px] font-mono uppercase text-[var(--color-text-muted)]">
              {s.access === "edit" ? "Edycja" : "Odczyt"}
            </span>
            <button
              onClick={() => revoke.mutate(s.id)}
              className="p-1.5 text-[var(--color-text-muted)] hover:text-[var(--color-primary)]"
              title="Odwołaj udostępnienie"
            >
              <Trash2 size={15} />
            </button>
          </li>
        );
      })}
    </ul>
  );
}
